import { Form } from 'radix-ui'
import { useCallback, useState } from 'react'
import './basic-player-information.scss'
import { FormFloatingFooter } from '@Components/claim-flow/form-floating-footer'
import { Button } from '@Components/button/button'
import PreviousChevron from '@Assets/icons/previous-chevron.svg'
import { Selector } from '@Components/selector/selector'
import { useHostConfig } from '../../../../hooks/use-host-config'
import { PlayerInfoFormData, PlayerInfoScreenProps } from './types'
import { CLASS_NAME } from './constants'
import { useAnnounce } from '../../../../hooks/use-announce'
import { Announcer } from '@Components/announcer/announcer'
import { AccessibleFormField } from '@Components/accessible-form-field/accessible-form-field'

export const EmailInfoScreen = ({
  onSubmit,
  onBack,
  formData,
  rescheduleFlow,
}: PlayerInfoScreenProps) => {
  const [consentCheckbox, setConsentCheckbox] = useState<boolean>(false)
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false)
  const [numberOfTickets, setNumberOfTickets] = useState<number | undefined>(
    formData?.numberOfTickets,
  )
  const hostConfig = useHostConfig()
  const { message, announce } = useAnnounce()

  const actionHandler = useCallback(
    (data: FormData) => {
      const formData: PlayerInfoFormData = {
        firstName: data.get('firstName')!.toString(),
        lastName: data.get('lastName')!.toString(),
        emailAddress: data.get('email')!.toString(),
        phoneNumber: '',
        numberOfTickets: numberOfTickets ?? 1,
      }

      onSubmit(formData)
    },
    [onSubmit, numberOfTickets],
  )

  return (
    <Form.Root asChild action={actionHandler}>
      <Form.Form className={CLASS_NAME}>
        <Announcer message={message} className={`${CLASS_NAME}__sr-alert`} />

        <section className={`${CLASS_NAME}__input-row`}>
          <AccessibleFormField
            name="firstName"
            label="First Name*"
            className={`${CLASS_NAME}__field`}
            labelClassName={`${CLASS_NAME}__label`}
            messageClassName={`${CLASS_NAME}__message`}
            inputClassName={`${CLASS_NAME}__input-control`}
            required
            announce={announce}
            messages={{ valueMissing: 'Please enter your first name' }}>
            {(inputProps) => (
              <input
                {...inputProps}
                type="text"
                placeholder="First Name"
                defaultValue={formData?.firstName}
                readOnly={rescheduleFlow}
                autoComplete="given-name"
              />
            )}
          </AccessibleFormField>

          <AccessibleFormField
            name="lastName"
            label="Last Name*"
            className={`${CLASS_NAME}__field`}
            labelClassName={`${CLASS_NAME}__label`}
            messageClassName={`${CLASS_NAME}__message`}
            inputClassName={`${CLASS_NAME}__input-control`}
            required
            announce={announce}
            messages={{ valueMissing: 'Please enter your last name' }}>
            {(inputProps) => (
              <input
                {...inputProps}
                type="text"
                placeholder="Last Name"
                defaultValue={formData?.lastName}
                readOnly={rescheduleFlow}
                autoComplete="family-name"
              />
            )}
          </AccessibleFormField>
        </section>

        <AccessibleFormField
          name="email"
          label="Email*"
          className={`${CLASS_NAME}__field`}
          labelClassName={`${CLASS_NAME}__label`}
          messageClassName={`${CLASS_NAME}__message`}
          inputClassName={`${CLASS_NAME}__input-control`}
          required
          announce={announce}
          messages={{
            valueMissing: 'Please enter your email',
            typeMismatch: 'Please provide a valid email address (e.g., name@example.com)',
          }}>
          {(inputProps) => (
            <input
              {...inputProps}
              type="email"
              placeholder="example@example.com"
              defaultValue={formData?.emailAddress}
              readOnly={rescheduleFlow}
              autoComplete="email"
            />
          )}
        </AccessibleFormField>

        {rescheduleFlow ? (
          <Form.Field name="numberOfTickets">
            <Form.Label className={`${CLASS_NAME}__label`}>Number of Tickets</Form.Label>

            <Form.Control name="numberOfTickets" asChild>
              <Selector<number>
                ariaLabel={'Number of tickets selector'}
                placeHolderText={''}
                value={numberOfTickets ? `${numberOfTickets}` : undefined}
                selectables={Array.from({ length: 15 }, (_, i) => {
                  const value = i + 1
                  return {
                    value,
                    label: value === 15 ? '15+' : value.toString(),
                  }
                })}
                onValueChange={setNumberOfTickets}
              />
            </Form.Control>
          </Form.Field>
        ) : null}

        {rescheduleFlow ? null : (
          <Form.Field name="consentCheckbox" className={`${CLASS_NAME}__consent-checkbox`}>
            <Form.Control asChild>
              <input
                type="checkbox"
                name="consentCheckbox"
                role="checkbox"
                aria-checked={consentCheckbox}
                onChange={(e) => setConsentCheckbox(e.target.checked)}
                required
              />
            </Form.Control>
            <Form.Label>{hostConfig.playerInformation.emailConsentLabel}</Form.Label>
          </Form.Field>
        )}

        {rescheduleFlow ? null : (
          <Form.Field name="agreeToTerms" className={`${CLASS_NAME}__consent-checkbox`}>
            <Form.Control asChild>
              <input
                type="checkbox"
                name="agreeToTerms"
                role="checkbox"
                aria-checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                required
              />
            </Form.Control>
            <Form.Label>{hostConfig.playerInformation.agreeToTermsLabel}</Form.Label>
          </Form.Field>
        )}

        <FormFloatingFooter>
          <section className={`floating-footer__buttons`}>
            <Button
              type="submit"
              className={`floating-footer__next-button`}
              disabled={rescheduleFlow ? false : !consentCheckbox || !agreeToTerms}>
              Next
            </Button>
            <Button
              className={`floating-footer__previous-button`}
              onClick={onBack}
              aria-label="Go back to previous step">
              <img src={PreviousChevron} alt="" aria-hidden="true" />
            </Button>
          </section>
        </FormFloatingFooter>
      </Form.Form>
    </Form.Root>
  )
}
